<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfMonth, endOfDay } from 'date-fns';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { useMetaCampaignsAdAccountsStore } from 'dashboard/stores/metaCampaignsAdAccounts';
import { useMetaCampaignsDashboardStore } from 'dashboard/stores/metaCampaignsDashboard';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMetaCampaignsFormatters } from '../composables/useMetaCampaignsFormatters';
import AdAccountCard from '../components/AdAccountCard.vue';
import CampaignTable from '../components/CampaignTable.vue';
import ConnectAdAccountModal from '../components/ConnectAdAccountModal.vue';

const { t } = useI18n();
const adAccountsStore = useMetaCampaignsAdAccountsStore();
const dashboardStore = useMetaCampaignsDashboardStore();
const { isAdmin } = useAdmin();
const { formatFullNumber } = useNumberFormatter();
const {
  brl,
  roasClass: sharedRoasClass,
  spendChartCollection,
} = useMetaCampaignsFormatters();

const BACKFILL_COOLDOWN_MS = 120000;
const POLL_INTERVAL_MS = 5000;

const connectModalRef = ref(null);
const selectedAdAccountId = ref(null);
const lastAutoBackfillAttempt = ref(new Map());
let pollTimer = null;

const now = new Date();
const dateRange = ref([startOfMonth(now), endOfDay(now)]);
const dateRangeType = ref(DATE_RANGE_TYPES.MONTH_TO_DATE);

const adAccounts = computed(() => adAccountsStore.getAdAccounts);
const dashboard = computed(() => dashboardStore.getDashboard);
const isLoading = computed(() => dashboardStore.uiFlags.fetchingDashboard);

const periodParams = computed(() => ({
  since: Math.floor(dateRange.value[0].getTime() / 1000),
  until: Math.floor(dateRange.value[1].getTime() / 1000),
  ...(selectedAdAccountId.value
    ? { ad_account_id: selectedAdAccountId.value }
    : {}),
}));

const selectedAdAccount = computed(
  () => adAccounts.value.find(a => a.id === selectedAdAccountId.value) || null
);

const isAdAccountSyncing = account =>
  account.sync_progress_percent !== null &&
  account.sync_progress_percent !== undefined;

const formatISODate = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(async () => {
    await adAccountsStore.fetch();
    if (!adAccounts.value.some(isAdAccountSyncing)) {
      stopPolling();
      dashboardStore.fetchDashboard(periodParams.value);
    }
  }, POLL_INTERVAL_MS);
};

const maybeTriggerBackfill = () => {
  const account = selectedAdAccount.value;
  if (!account || isAdAccountSyncing(account)) return;

  const requestedSince = dateRange.value[0];
  const earliest = account.earliest_synced_date
    ? new Date(`${account.earliest_synced_date}T00:00:00`)
    : null;
  const hasGap = !earliest || requestedSince < earliest;
  if (!hasGap) return;

  const lastAttempt = lastAutoBackfillAttempt.value.get(account.id);
  if (lastAttempt && Date.now() - lastAttempt < BACKFILL_COOLDOWN_MS) return;
  lastAutoBackfillAttempt.value.set(account.id, Date.now());

  const since = formatISODate(requestedSince);
  const until = formatISODate(
    earliest ? new Date(earliest.getTime() - 86400000) : new Date()
  );

  adAccountsStore
    .backfill(account.id, since, until)
    .then(startPolling)
    .catch(() => {});
};

const loadDashboard = () => {
  dashboardStore.fetchDashboard(periodParams.value);
  maybeTriggerBackfill();
};

const roasClass = computed(() =>
  sharedRoasClass(dashboard.value?.summary?.roas, 'text-n-slate-12')
);

// Be explicit about how much of the period's revenue could actually be traced
// back to a campaign, instead of implying full coverage.
const attributionCoverageNote = computed(() => {
  const summary = dashboard.value?.summary;
  if (!summary?.revenue_total) return '';

  const unattributed = summary.revenue_total - summary.revenue;
  if (unattributed <= 0) return '';

  return t('META_CAMPAIGNS.DASHBOARD.ATTRIBUTION_COVERAGE', {
    attributed: brl(summary.revenue),
    total: brl(summary.revenue_total),
  });
});

const chartCollection = computed(() =>
  spendChartCollection(
    dashboard.value?.by_day || [],
    t('META_CAMPAIGNS.DASHBOARD.CHART.SPEND')
  )
);

function onDateRangeChanged([start, end, rangeType]) {
  dateRange.value = [start, end];
  dateRangeType.value = rangeType;
  loadDashboard();
}

const handleAdAccountConnected = () => {
  adAccountsStore.fetch();
  loadDashboard();
};

onMounted(() => {
  adAccountsStore.fetch();
  loadDashboard();
});

onUnmounted(stopPolling);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-weak"
    >
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between md:h-16 md:py-0"
        >
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('META_CAMPAIGNS.DASHBOARD.TITLE') }}
          </h1>
          <div class="flex items-center gap-2 flex-wrap">
            <WootDatePicker
              v-model:date-range="dateRange"
              v-model:range-type="dateRangeType"
              @date-range-changed="onDateRangeChanged"
            />
            <Button
              v-if="isAdmin"
              size="sm"
              icon="i-lucide-plus"
              :label="t('META_CAMPAIGNS.DASHBOARD.CONNECT_AD_ACCOUNT')"
              @click="connectModalRef?.open()"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6 flex flex-col gap-6">
        <div v-if="adAccounts.length" class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('META_CAMPAIGNS.DASHBOARD.AD_ACCOUNTS_SECTION') }}
            </h2>
            <select
              v-model="selectedAdAccountId"
              class="w-auto shrink-0 self-center h-7 !py-0 px-2 leading-7 text-xs rounded border border-n-strong bg-n-surface-1 text-n-slate-11 focus:outline-none cursor-pointer"
              @change="loadDashboard"
            >
              <option :value="null">
                {{ t('META_CAMPAIGNS.DASHBOARD.ALL_AD_ACCOUNTS') }}
              </option>
              <option
                v-for="account in adAccounts"
                :key="account.id"
                :value="account.id"
              >
                {{ account.name }}
              </option>
            </select>
          </div>
          <AdAccountCard
            v-for="account in adAccounts"
            :key="account.id"
            :ad-account="account"
          />
        </div>

        <div
          v-else
          class="rounded-xl border border-dashed border-n-strong p-8 text-center"
        >
          <p class="text-sm text-n-slate-10 mb-3">
            {{ t('META_CAMPAIGNS.DASHBOARD.NO_AD_ACCOUNTS') }}
          </p>
          <Button
            v-if="isAdmin"
            size="sm"
            :label="t('META_CAMPAIGNS.DASHBOARD.CONNECT_AD_ACCOUNT')"
            @click="connectModalRef?.open()"
          />
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <span
            class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9"
          />
        </div>

        <template v-else-if="dashboard">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ brl(dashboard.summary.spend) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.SPEND') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatFullNumber(dashboard.summary.impressions) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.IMPRESSIONS') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatFullNumber(dashboard.summary.clicks) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.CLICKS') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatFullNumber(dashboard.summary.ctr) }}%
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.CTR') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ brl(dashboard.summary.cpc) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.CPC') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatFullNumber(dashboard.summary.reach) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.REACH') }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatFullNumber(dashboard.summary.leads) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.LEADS') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ brl(dashboard.summary.revenue) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.REVENUE') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold" :class="roasClass">
                {{
                  dashboard.summary.roas
                    ? `${formatFullNumber(dashboard.summary.roas)}x`
                    : '—'
                }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.ROAS') }}
              </p>
            </div>
            <div
              class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center"
            >
              <p class="text-2xl font-bold text-n-slate-12">
                {{ brl(dashboard.summary.cac) }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1.5">
                {{ t('META_CAMPAIGNS.DASHBOARD.STATS.CAC') }}
              </p>
            </div>
          </div>

          <p
            v-if="attributionCoverageNote"
            class="text-xs text-n-slate-10 -mt-2"
          >
            {{ attributionCoverageNote }}
          </p>

          <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-4">
              {{ t('META_CAMPAIGNS.DASHBOARD.CHART.TITLE') }}
            </h2>
            <div class="h-64">
              <LineChart
                v-if="dashboard.by_day.length"
                :collection="chartCollection"
              />
              <p v-else class="text-sm text-n-slate-9">
                {{ t('META_CAMPAIGNS.DASHBOARD.CHART.NO_DATA') }}
              </p>
            </div>
          </div>

          <CampaignTable
            :campaigns="dashboard.campaigns"
            :period="{ since: periodParams.since, until: periodParams.until }"
          />
        </template>
      </div>
    </main>

    <ConnectAdAccountModal
      ref="connectModalRef"
      @connected="handleAdAccountConnected"
    />
  </section>
</template>
